import {ActionIcon, Button, Group, TextInput} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconSearch, IconX} from '@tabler/icons-react'
import {useNavigate} from '@tanstack/react-router'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'

import type {WorkerType} from './schemas'
import {useCreateWorkerType, useWorkerTypes} from './use-worker-types'
import {WorkerTypeFormModal} from './worker-type-form-modal'

function hasActiveFilters(name: string) {
    return Boolean(name.trim())
}

function filterWorkerTypes(types: WorkerType[], nameFilter: string) {
    const name = nameFilter.trim().toLowerCase()
    if (!name) {
        return types
    }
    return types.filter((type) => type.name.toLowerCase().includes(name))
}

function compareWorkerTypes(
    left: WorkerType,
    right: WorkerType,
    columnAccessor: DataTableSortStatus<WorkerType>['columnAccessor'],
) {
    const leftValue = String(left[columnAccessor as keyof WorkerType] ?? '')
    const rightValue = String(right[columnAccessor as keyof WorkerType] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortWorkerTypes(types: WorkerType[], sortStatus: DataTableSortStatus<WorkerType>) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...types].sort(
        (left, right) =>
            directionMultiplier * compareWorkerTypes(left, right, sortStatus.columnAccessor),
    )
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

export function WorkerTypesPage() {
    const navigate = useNavigate()
    const user = useCurrentUser()
    const [nameFilter, setNameFilter] = useState('')
    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const {data: workerTypes = [], isLoading} = useWorkerTypes()
    const createWorkerType = useCreateWorkerType()
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<WorkerType>>({
        columnAccessor: 'name',
        direction: 'asc',
    })
    const [createModalOpen, setCreateModalOpen] = useState(false)

    const filtersActive = hasActiveFilters(debouncedName)

    const sortedTypes = useMemo(
        () => sortWorkerTypes(filterWorkerTypes(workerTypes, debouncedName), sortStatus),
        [workerTypes, debouncedName, sortStatus],
    )

    const columns = useMemo((): DataTableColumn<WorkerType>[] => {
        return [
            {
                accessor: 'name',
                title: 'Name',
                sortable: true,
                filter: (
                    <TextInput
                        label="Type name"
                        description="Show types whose names include the specified text"
                        placeholder="Search type names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(nameFilter, () => setNameFilter(''))}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: nameFilter !== '',
            },
        ]
    }, [nameFilter])

    return (
        <>
            <Group justify="flex-end" mb="md">
                {isAdmin(user) && (
                    <Button onClick={() => setCreateModalOpen(true)}>Create type</Button>
                )}
            </Group>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                idAccessor="id"
                records={sortedTypes}
                columns={columns}
                fetching={isLoading}
                minHeight={sortedTypes.length === 0 ? 180 : undefined}
                noRecordsText={
                    filtersActive ? 'No types match your filters.' : 'No worker types yet.'
                }
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                onRowClick={({record}) =>
                    void navigate({
                        to: '/workers/types/$typeId',
                        params: {typeId: record.id},
                    })
                }
            />

            <WorkerTypeFormModal
                opened={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onCreate={(input) => createWorkerType.mutateAsync(input)}
                onSaved={() => setCreateModalOpen(false)}
            />
        </>
    )
}
